import { useState, useEffect, useRef } from 'react';
import { Upload, X } from 'lucide-react';

/**
 * The shared "a card has a 1:1 and a 4:5 crop" model, used by both the Meta and
 * LinkedIn carousels.
 *
 * Both formats treat 4:5 the same way: not an extra card, but an alternative
 * rendering of an existing one, sharing that card's text and its position in the
 * strip. The only real difference between them is media policy — Meta cards accept
 * video, LinkedIn cards are images only — so that is a parameter here rather than
 * a reason to keep two copies of this logic.
 */

/** The two feed crops. 9:16 story cards are a separate, single-crop section. */
export type FeedRatio = '1x1' | '4x5';
export const FEED_RATIOS: FeedRatio[] = ['1x1', '4x5'];

/**
 * A single crop of a card. The two crops can only be filled or cleared
 * independently — never reordered or deleted independently, since they are one
 * card.
 *
 * Each slot is separately new-or-existing: a card can have a saved 1:1 and a
 * freshly picked 4:5 at the same time, which is why "new | existing" lives here
 * rather than on the card.
 */
export type CardSlot =
  | { kind: 'empty' }
  | { kind: 'new'; file: File }
  | { kind: 'existing'; existingAssetId: string; previewUrl: string; contentType?: string | null };

export const EMPTY_SLOT: CardSlot = { kind: 'empty' };

export function emptySlots(): Record<FeedRatio, CardSlot> {
  return { '1x1': EMPTY_SLOT, '4x5': EMPTY_SLOT };
}

export function slotFile(slot: CardSlot): File | null {
  return slot.kind === 'new' ? slot.file : null;
}

export function isSlotFilled(slot: CardSlot): boolean {
  return slot.kind !== 'empty';
}

/** The existing asset id behind a slot, if it has one saved server-side. */
export function slotExistingId(slot: CardSlot): string | null {
  return slot.kind === 'existing' ? slot.existingAssetId : null;
}

function looksLikeVideo(slot: CardSlot): boolean {
  if (slot.kind === 'new') return slot.file.type.startsWith('video/');
  if (slot.kind !== 'existing') return false;
  return !!(
    slot.contentType?.startsWith('video/') ||
    ['.mp4', '.mov', '.webm'].some((ext) =>
      slot.previewUrl.toLowerCase().split('?')[0].endsWith(ext),
    )
  );
}

/**
 * One upload thumbnail for one crop of one card. A feed card renders two of
 * these side by side.
 */
export function SlotThumb({
  slot,
  ratio,
  index,
  isActive,
  accept,
  allowVideo = true,
  filterFiles,
  onFileChange,
  onSelect,
}: {
  slot: CardSlot;
  ratio: FeedRatio;
  index: number;
  /** True when this exact crop is the one being shown in the mockup. */
  isActive: boolean;
  accept: string;
  /** Images-only formats render through <img>, which breaks on a video blob. */
  allowVideo?: boolean;
  /**
   * Screens files picked here. `accept` is only a hint and does not cover
   * drag-and-drop or Gear Exchange, so formats that reject media types pass the
   * same filter they use on those paths. Returning an empty list drops the pick.
   */
  filterFiles?: (files: File[]) => File[];
  onFileChange: (f: File | null) => void;
  onSelect: () => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);

  const file = slotFile(slot);
  const [blobSrc, setBlobSrc] = useState<string | undefined>(undefined);
  useEffect(() => {
    if (!file) {
      setBlobSrc(undefined);
      return;
    }
    const url = URL.createObjectURL(file);
    setBlobSrc(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const isVideo = allowVideo && looksLikeVideo(slot);
  const thumbnailUrl = slot.kind === 'existing' ? slot.previewUrl : blobSrc;

  // Both crops get the same box. Sizing the 4:5 thumb to its true aspect made it
  // visibly smaller than the 1:1 one, which reads as "less important" rather than
  // "narrower" — and the ratio badge already says which crop this is. The mockup
  // is where the real aspect is shown.
  const thumbClass = 'w-[104px] h-[104px] flex-shrink-0';

  return (
    <div
      className={`${thumbClass} relative group/thumb rounded-lg overflow-hidden bg-slate-100 border cursor-pointer transition-colors ${
        isActive
          ? 'border-[#0014cc]/50 ring-2 ring-[#0014cc]/30'
          : 'border-slate-200 hover:border-slate-300'
      }`}
      onClick={(e) => {
        e.stopPropagation();
        // Clicking the body of a filled crop only switches which one the mockup
        // shows — uploading is the explicit corner button, so selecting can't
        // pop a file dialog by accident. An empty crop has nothing to preview,
        // so it goes straight to the picker.
        if (thumbnailUrl) onSelect();
        else inputRef.current?.click();
      }}
      title={thumbnailUrl ? `Show ${ratio} in preview` : `Upload ${ratio} image`}
    >
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (!f) return;
          if (filterFiles && filterFiles([f]).length === 0) {
            e.target.value = '';
            return;
          }
          onFileChange(f);
          // Clear so re-picking the same file still fires change — otherwise
          // replacing a crop with an edited copy of itself silently does nothing.
          e.target.value = '';
        }}
      />
      {thumbnailUrl ? (
        <>
          {isVideo ? (
            <video
              src={thumbnailUrl}
              className="w-full h-full object-cover"
              muted
              playsInline
              preload="auto"
              onLoadedData={(e) => {
                e.currentTarget.currentTime = 0.1;
              }}
            />
          ) : (
            <img
              src={thumbnailUrl}
              alt={`Card ${index + 1} ${ratio}`}
              className="w-full h-full object-cover"
            />
          )}
          {/* Hover tint only — purely decorative, so it must not swallow the
              click that selects this crop. Replace/clear are the corner buttons. */}
          <div className="absolute inset-0 bg-black/20 opacity-0 group-hover/thumb:opacity-100 transition-opacity pointer-events-none" />
          {/* Replace image */}
          <button
            type="button"
            title="Replace image"
            onClick={(e) => {
              e.stopPropagation();
              inputRef.current?.click();
            }}
            className="absolute bottom-1 right-1 w-5 h-5 rounded-full bg-black/60 hover:bg-black/80 text-white flex items-center justify-center opacity-0 group-hover/thumb:opacity-100 transition-opacity z-10"
          >
            <Upload className="w-2.5 h-2.5" />
          </button>
          {/* Clear image — inside bounds so hover stays active */}
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onFileChange(null);
            }}
            className="absolute top-1 right-1 w-4 h-4 rounded-full bg-red-500 hover:bg-red-600 text-white flex items-center justify-center opacity-0 group-hover/thumb:opacity-100 transition-opacity z-10"
          >
            <X className="w-2.5 h-2.5" />
          </button>
        </>
      ) : (
        <div className="w-full h-full flex flex-col items-center justify-center gap-1">
          <Upload className="w-4 h-4 text-slate-400" />
          <span className="text-[9px] text-slate-400 font-medium leading-none">{ratio}</span>
        </div>
      )}
      {/* Ratio badge doubles as the active-crop indicator, so it is obvious
          which of the two the mockup is currently showing. */}
      <div
        className={`absolute top-1 left-1 px-1 h-4 rounded-full text-[9px] font-bold flex items-center justify-center transition-colors ${
          isActive ? 'bg-[#0014cc] text-white' : 'bg-black/25 text-white'
        }`}
      >
        {ratio}
      </div>
    </div>
  );
}
